import { useState } from 'react';
import { useMutation } from '@apollo/client';
import AWS from 'aws-sdk';
import imageCompression from 'browser-image-compression';

import { GENERATE_IDENTITY_TOKEN } from '../../graphql/mutations/uploadMutation';
import helpers from 'helpers';

let currentCredentials;

// Security constants
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
// Blocked executable file extensions
const BLOCKED_EXTENSIONS = [
  // Windows executables
  'exe',
  'bat',
  'cmd',
  'com',
  'pif',
  'scr',
  'msi',
  'dll',
  // Unix/Linux executables
  'sh',
  'bash',
  'bin',
  'run',
  'deb',
  'rpm',
  // macOS executables
  'app',
  'dmg',
  'pkg',
  // Script executables
  'jar',
  'vbs',
  'js',
  'jsx',
  'ts',
  'tsx',
  // Other potentially dangerous
  'php',
  'asp',
  'aspx',
  'jsp',
];

/**
 * Sanitizes file path to prevent path traversal attacks
 * @param {string} path - The path to sanitize
 * @returns {string} - Sanitized path
 */
const sanitizePath = (path) => {
  if (!path || typeof path !== 'string') {
    throw new Error('Invalid path provided');
  }

  // Remove path traversal sequences
  let sanitized = path
    .replace(/\.\./g, '') // Remove ..
    .replace(/\/\//g, '/') // Remove double slashes
    .replace(/^\/+|\/+$/g, '') // Remove leading/trailing slashes
    .replace(/[^a-zA-Z0-9\-_/]/g, ''); // Remove dangerous characters

  // Limit path length
  if (sanitized.length > 200) {
    sanitized = sanitized.substring(0, 200);
  }

  return sanitized || 'uploads';
};

/**
 * Sanitizes filename to prevent injection attacks
 * @param {string} filename - The filename to sanitize
 * @returns {string} - Sanitized filename
 */
const sanitizeFilename = (filename) => {
  if (!filename || typeof filename !== 'string') {
    return 'file';
  }

  // Remove path separators and dangerous characters
  let sanitized = filename
    .replace(/\.\./g, '') // Remove path traversal
    .replace(/[/\\]/g, '_') // Replace slashes with underscores
    .replace(/[^a-zA-Z0-9\-_.]/g, '') // Remove special characters
    .substring(0, 255); // Limit length

  return sanitized || 'file';
};

/**
 * Validates file extension - blocks executable files
 * @param {string} extension - File extension
 * @returns {boolean} - True if extension is not blocked (not an executable)
 */
const isValidExtension = (extension) => {
  if (!extension) return false;
  const ext = extension.toLowerCase().replace(/^\./, '');
  return !BLOCKED_EXTENSIONS.includes(ext);
};

/**
 * Validates file type - allows all file types except executables
 * @param {string} mimeType - MIME type
 * @returns {boolean} - True if MIME type is allowed (all non-executable types)
 */
const isValidFileType = (mimeType) => {
  // Allow all file types - we only block by extension
  return true;
};

/**
 * Validates file size
 * @param {number} size - File size in bytes
 * @returns {boolean} - True if size is within limits
 */
const isValidFileSize = (size) => {
  return size > 0 && size <= MAX_FILE_SIZE;
};

/**
 * Gets safe file extension from filename
 * @param {string} filename - Original filename
 * @param {string} mimeType - MIME type (optional, for fallback)
 * @returns {string} - Safe file extension
 */
const getSafeExtension = (filename, mimeType) => {
  // Try to get extension from filename
  const filenameExt = filename.split('.').pop()?.toLowerCase();
  if (filenameExt && isValidExtension(filenameExt)) {
    return filenameExt;
  }

  // If no valid extension found, try to infer from MIME type
  if (mimeType) {
    const mimeToExt = {
      'image/jpeg': 'jpg',
      'image/jpg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'image/webp': 'webp',
      'image/svg+xml': 'svg',
      'application/pdf': 'pdf',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'text/plain': 'txt',
      'text/csv': 'csv',
    };
    const ext = mimeToExt[mimeType?.toLowerCase()];
    if (ext && isValidExtension(ext)) {
      return ext;
    }
  }

  // Default fallback
  return 'bin';
};

/**
 * Extracts proposal ID from URL if on client preview or PDF page
 * @returns {string|null} - Proposal ID if found, null otherwise
 */
const extractProposalIdFromUrl = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const { pathname } = window.location;

  // Client preview URL pattern: /cd/:cid
  const clientPreviewMatch = pathname.match(/^\/cd\/([^/]+)/);
  if (clientPreviewMatch) {
    return clientPreviewMatch[1];
  }

  // PDF URL pattern: /PDF/:cid
  const pdfMatch = pathname.match(/^\/PDF\/([^/]+)/);
  if (pdfMatch) {
    return pdfMatch[1];
  }

  // Published proposal URL pattern: /pd/:id
  const publishedMatch = pathname.match(/^\/pd\/([^/]+)/);
  if (publishedMatch) {
    return publishedMatch[1];
  }

  // Check for query parameter (e.g., /proposal-preview?proposalId=...)
  const urlParams = new URLSearchParams(window.location.search);
  const proposalIdParam = urlParams.get('proposalId');
  if (proposalIdParam) {
    return proposalIdParam;
  }

  return null;
};

/**
 * Uploader component
 * @returns {Array} - Array containing upload function and status object
 */
const Uploader = () => {
  const [status, setStatus] = useState({
    list: {},
    asArray: [],
    total: 0,
    loaded: 0,
    percent: 0,
    status: 'ready',
  });

  const [generateToken] = useMutation(GENERATE_IDENTITY_TOKEN);

  const _generateTokens = async () => {
    return new Promise(async (resolve, reject) => {
      try {
        // Extract proposal ID from URL if on client preview or PDF page
        const proposalId = extractProposalIdFromUrl();

        generateToken({
          variables: {
            proposalId: proposalId || undefined,
          },
        })
          .then(({ data: { generateIdentityToken } }) => {
            const params = {
              IdentityId: generateIdentityToken.IdentityId,
              Logins: {
                'cognito-identity.amazonaws.com': generateIdentityToken.Token,
              },
            };
            const cognitoidentity = new AWS.CognitoIdentity({
              region: process.env.REACT_APP_AWS_REGION,
            });
            cognitoidentity.getCredentialsForIdentity(params, (err, data) => {
              err ? reject(err) : resolve(data);
            });
          })
          .catch((error) => {
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
  };

  const _isExpired = (date) => {
    if (!date) return true;
    return date.getTime() <= new Date().getTime();
  };

  const _getCredentials = async () => {
    if (
      !currentCredentials ||
      !currentCredentials.Credentials ||
      _isExpired(currentCredentials.Credentials.Expiration)
    ) {
      const result = await _generateTokens();
      currentCredentials = result;
    }

    return new AWS.Credentials(
      currentCredentials.Credentials.AccessKeyId,
      currentCredentials.Credentials.SecretKey,
      currentCredentials.Credentials.SessionToken
    );
  };

  const _getS3 = async () => {
    const credentials = await _getCredentials();
    const s3 = new AWS.S3({
      credentials,
      region: process.env.REACT_APP_AWS_REGION,
    });
    return s3;
  };

  const uploadFile = (file, s3, path, handleProgress, fileIndex) =>
    new Promise(async (resolve, reject) => {
      handleProgress(0, fileIndex);

      let compressedFile = file;
      let keyName = helpers.objectId();

      const SKIP_COMPRESSION_THRESHOLD = 500 * 1024; // 500KB — already small enough
      const MIN_COMPRESSION_RATIO = 0.2; // don't allow more than 80% size reduction

      try {
        // Security: Validate file size
        if (!isValidFileSize(file.size)) {
          reject(
            new Error(
              `File size exceeds maximum allowed size of ${MAX_FILE_SIZE / (1024 * 1024)}MB`
            )
          );
          return;
        }

        // Security: Validate file extension (block executables)
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        if (!fileExtension || !isValidExtension(fileExtension)) {
          reject(new Error('Executable files are not allowed for security reasons.'));
          return;
        }

        // Security: Validate file type
        if (!isValidFileType(file.type)) {
          reject(new Error('Invalid file type.'));
          return;
        }

        // Security: Sanitize path to prevent path traversal
        const sanitizedPath = sanitizePath(path);

        // Security: Sanitize filename
        const sanitizedFilename = sanitizeFilename(file.name);

        // Security: Get safe extension
        const safeExtension = getSafeExtension(file.name, file.type);
        if (!safeExtension || !isValidExtension(safeExtension)) {
          reject(new Error('Invalid file extension. Executable files are not allowed.'));
          return;
        }

        // dont compress gif/non-image files, or files already small enough
        const isImageFile = file.type && file.type.startsWith('image/');
        if (isImageFile && file.type !== 'image/gif' && file.size > SKIP_COMPRESSION_THRESHOLD) {
          try {
            const attempt = await imageCompression(file, {
              useWebWorker: true,
              initialQuality: 0.85,
            });
            // if compression was too aggressive, keep original
            compressedFile = attempt.size / file.size < MIN_COMPRESSION_RATIO ? file : attempt;
          } catch (error) {
            console.log(`File can't be compressed, using original`);
          }
        }

        // Security: Re-validate after compression
        if (!isValidFileSize(compressedFile.size)) {
          reject(
            new Error(
              `Compressed file size exceeds maximum allowed size of ${
                MAX_FILE_SIZE / (1024 * 1024)
              }MB`
            )
          );
          return;
        }

        // Security: Use sanitized values for S3 key
        const s3Key = `${sanitizedPath}/${keyName}.${safeExtension}`;

        const params = {
          ACL: 'public-read',
          Key: s3Key,
          ContentType: compressedFile.type || file.type,
          Body: compressedFile,
          CacheControl: 'no-cache',
          Bucket: process.env.REACT_APP_AWS_BUCKET,
        };

        s3.upload(params)
          .on('httpUploadProgress', (progress) => {
            let percentage = Math.round((progress.loaded / progress.total) * 100);
            handleProgress(percentage, fileIndex);
          })
          .on('success', (data) => {
            resolve({
              url: `${process.env.REACT_APP_CLOUD_FRONT_URL}${sanitizedPath}/${keyName}.${safeExtension}`,
              filename: sanitizedFilename,
              filetype: compressedFile.type || file.type,
            });
          })
          .send((error, data) => {
            if (error) {
              reject(error);
            } else if (data) {
              resolve({
                url: `${process.env.REACT_APP_CLOUD_FRONT_URL}${sanitizedPath}/${keyName}.${safeExtension}`,
                filename: sanitizedFilename,
                filetype: compressedFile.type || file.type,
              });
            }
          });
      } catch (error) {
        reject(error);
      }
    });

  const upload = (files, path) => {
    if (status.status === 'uploading') return status;

    setStatus({
      percent: 0,
      status: 'uploading',
    });

    return new Promise(async (resolve, reject) => {
      try {
        const s3 = await _getS3();

        if (files.length) {
          const progresses = files.map((f) => 0);
          const progressHandler = (percentage, index) => {
            progresses[index] = percentage;
            let totalProgress = 0;

            progresses.forEach((prog) => {
              totalProgress += prog / progresses.length;
            });
            totalProgress = Math.round(totalProgress);
            setStatus({
              percent: totalProgress,
              status: 'uploading',
            });
          };

          const urls = await Promise.all(
            Object.values(files).map(
              async (file, index) => await uploadFile(file, s3, path, progressHandler, index)
            )
          );
          resolve(urls);
        } else {
          const progressHandler = (percentage) => {
            setStatus({
              percent: percentage,
              status: 'uploading',
            });
          };

          const url = await uploadFile(files, s3, path, progressHandler, 0);
          resolve(url);
        }

        setStatus({
          percent: 100,
          status: 'completed',
        });
      } catch (error) {
        const errorState = {
          error,
          status: 'error',
        };
        setStatus((prevState) => ({
          ...prevState,
          ...errorState,
        }));
        reject(error);
      }
    });
  };

  return [upload, status];
};

export default Uploader;
